import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import RichTextEditor from '@/components/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MessageCircle, Send, Eye, EyeOff, Info, Loader2, ArrowLeft, Shield } from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

// Optional inspiration prompts (slug → writing hints). These are ONLY used
// as placeholder + title-suggestion — never inserted into the content body,
// so the editor stays clean and the user writes freely.
const PROMPTS = {
  fantasia: {
    label: '🔥 Uma fantasia que nunca contei',
    titleHint: 'A fantasia que nunca contei',
    placeholder: 'É uma fantasia que me visita há muito tempo… Começa por descrever a cena que te aparece na cabeça. Onde? Com quem? O que sentes?',
  },
  segredo: {
    label: '😈 Algo que fiz e ninguém sabe',
    titleHint: 'Algo que fiz e ninguém sabe',
    placeholder: 'Aconteceu numa noite qualquer, e ninguém no meu círculo faz ideia. Conta o que se passou, como te sentiste, e porque nunca partilhaste até agora.',
  },
  desejo: {
    label: '💭 Algo que desejo experimentar',
    titleHint: 'O que ainda desejo experimentar',
    placeholder: 'Há algo que quero mesmo viver, ainda não vivi. Descreve o desejo, o que te trava, e como imaginas o momento em que finalmente acontece.',
  },
  pessoa: {
    label: '❤️ Uma pessoa que não consigo esquecer',
    titleHint: 'A pessoa que não consigo esquecer',
    placeholder: 'Já passou tempo, e continuo a lembrar-me dela/dele. Descreve quem é, o que ficou por dizer, e o que ainda sentes quando pensas.',
  },
  vergonha: {
    label: '🙈 Uma situação que me deixou com vergonha',
    titleHint: 'A situação que me deixou com vergonha',
    placeholder: 'Ainda me sinto quente quando penso nisto. Conta a situação por ordem: onde, com quem, o que aconteceu — e o que ficou no teu corpo depois.',
  },
};

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export default function SubmitConfession() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const promptSlug = searchParams.get('prompt');
  const prompt = promptSlug ? PROMPTS[promptSlug] : null;

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    is_anonymous: true
  });

  useEffect(() => {
    api.get('/confessions/categories').then(res => {
      setCategories(res.data.map(c => ({ value: c.name, label: c.name })));
    }).catch(() => {});
  }, []);

  const plainContent = stripHtml(formData.content);
  const charCount = plainContent.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Precisa de estar autenticado para partilhar');
      navigate('/login');
      return;
    }

    if (!formData.title.trim() || !plainContent.trim()) {
      toast.error('Preenche o título e o conteúdo');
      return;
    }

    if (charCount < 50) {
      toast.error('A confissão deve ter pelo menos 50 caracteres');
      return;
    }

    setLoading(true);
    try {
      await api.post('/confessions/submit', formData);
      toast.success('Confissão enviada para revisão!');
      navigate('/confessions');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao enviar confissão'));
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 pt-24 pb-12">
          <Card className="bg-surface border-border">
            <CardContent className="py-12 text-center">
              <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">Autenticação Necessária</h2>
              <p className="text-muted-foreground mb-6">
                Precisa de estar autenticado para partilhar uma confissão.
                A sua identidade ficará protegida publicamente.
              </p>
              <Button onClick={() => navigate('/login')} className="bg-ruby hover:bg-ruby-light">
                Entrar
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/confessions')}
          className="mb-6 text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar às Confissões
        </Button>

        <Card className="bg-surface border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-ruby" />
              Partilhar Confissão
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Optional inspiration banner (from ?prompt=<slug>) — pure UI hint,
                the confession body stays empty so the writer starts clean. */}
            {prompt && (
              <div className="bg-ruby/5 border border-ruby/30 rounded-lg p-4 mb-6" data-testid={`confession-prompt-banner-${promptSlug}`}>
                <p className="text-xs uppercase tracking-widest text-ruby mb-1">Inspiração</p>
                <p className="text-foreground font-medium">{prompt.label}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  É apenas uma pista. Podes ignorá-la e escrever o que quiseres.
                </p>
              </div>
            )}

            {/* Privacy Notice */}
            <div className="bg-ruby/10 border border-ruby/30 rounded-lg p-4 mb-6 flex gap-3">
              <Info className="w-5 h-5 text-ruby flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-ruby font-medium mb-1">Informação sobre privacidade</p>
                <p className="text-muted-foreground">
                  A sua confissão será revista pela nossa equipa antes de ser publicada.
                  Se escolher a opção anónimo, a sua identidade <strong className="text-foreground">não será visível publicamente</strong>.
                  No entanto, por razões de segurança e moderação, o Intimate.pt regista 
                  internamente o autor de todas as confissões. Ao submeter, aceita esta condição.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  placeholder={prompt ? prompt.titleHint : 'Um título cativante para a tua confissão...'}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-background border-border"
                  maxLength={100}
                  data-testid="confession-title"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {formData.title.length}/100
                </p>
              </div>

              {/* Excerpt / Summary */}
              <div className="space-y-2">
                <Label htmlFor="excerpt">Resumo (aparece na listagem)</Label>
                <Input
                  id="excerpt"
                  placeholder="Breve resumo que desperte curiosidade..."
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="bg-background border-border"
                  maxLength={200}
                  data-testid="confession-excerpt"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {formData.excerpt.length}/200
                </p>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Categoria (opcional)</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="confession-category">
                    <SelectValue placeholder="Seleciona uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label>A Sua Confissão</Label>
                <RichTextEditor
                  content={formData.content}
                  onChange={(html) => setFormData({ ...formData, content: html })}
                  placeholder={prompt ? prompt.placeholder : 'Partilha a sua história... seja honesto, seja tu mesmo.'}
                  minHeight="200px"
                  hideImage
                />
                <p className="text-xs text-muted-foreground text-right">
                  {charCount} caracteres (mínimo 50)
                </p>
              </div>

              {/* Anonymous Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  {formData.is_anonymous ? (
                    <EyeOff className="w-5 h-5 text-ruby" />
                  ) : (
                    <Eye className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium text-foreground">Publicar Anonimamente</p>
                    <p className="text-sm text-muted-foreground">
                      {formData.is_anonymous 
                        ? 'O seu nome não será mostrado' 
                        : 'O seu nome será visível'
                      }
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_anonymous}
                  onCheckedChange={(v) => setFormData({ ...formData, is_anonymous: v })}
                  data-testid="confession-anonymous"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading || !formData.title.trim() || charCount < 50}
                className="w-full bg-ruby hover:bg-ruby-light py-6"
                data-testid="submit-confession-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Enviar para Revisão
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
